import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  BookOpen,
  FileText,
  Users,
  TrendingUp,
  Building2,
  Shield,
  ArrowRight,
  Search,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Target,
  Award,
  Clock,
  CheckCircle,
  Play,
  Download,
  Mail,
  Phone,
  Globe,
  Linkedin,
  Twitter,
} from "lucide-react";

export default function DocsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const documentCategories = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: Play,
      color: "from-emerald-500 to-teal-600",
      documents: [
        {
          title: "User Guide",
          description: "Complete guide to using MyMeetingsPal for your corporate meetings",
          path: "/docs/user-guide",
          readTime: "15 min read",
          tags: ["Basics", "Tutorial"],
        },
        {
          title: "Quick Start",
          description: "Get up and running in 5 minutes",
          path: "/docs/user-guide#quick-start",
          readTime: "5 min read",
          tags: ["Quick", "Setup"],
        },
      ],
    },
    {
      id: "company",
      title: "About MyMeetingsPal",
      icon: Building2,
      color: "from-blue-500 to-indigo-600",
      documents: [
        {
          title: "About Us",
          description: "Our mission, team, and the problem we're solving",
          path: "/docs/about",
          readTime: "8 min read",
          tags: ["Company", "Mission"],
        },
        {
          title: "Product Overview",
          description: "Features and capabilities of the platform",
          path: "/docs/about#features",
          readTime: "6 min read",
          tags: ["Features", "Overview"],
        },
      ],
    },
    {
      id: "investors",
      title: "For Investors",
      icon: TrendingUp,
      color: "from-purple-500 to-violet-600",
      documents: [
        {
          title: "Investor Information",
          description: "Market opportunity, traction, and investment details",
          path: "/docs/investors",
          readTime: "12 min read",
          tags: ["Investment", "Financials"],
        },
        {
          title: "Market Analysis",
          description: "TAM/SAM/SOM and competitive landscape",
          path: "/docs/investors#market",
          readTime: "8 min read",
          tags: ["Market", "Analysis"],
        },
      ],
    },
    {
      id: "governance",
      title: "Governance & Compliance",
      icon: Shield,
      color: "from-amber-500 to-orange-600",
      documents: [
        {
          title: "Governance Features",
          description: "Chair signing, locking, audit trails for public companies",
          path: "/docs/user-guide#governance",
          readTime: "10 min read",
          tags: ["Governance", "Compliance"],
        },
        {
          title: "Security & Privacy",
          description: "How we protect your data and ensure compliance",
          path: "/docs/about#security",
          readTime: "7 min read",
          tags: ["Security", "Privacy"],
        },
      ],
    },
  ];

  const featuredDocs = [
    {
      title: "User Guide",
      subtitle: "Everything you need to know",
      icon: BookOpen,
      path: "/docs/user-guide",
      color: "bg-gradient-to-br from-teal-500 to-emerald-600",
    },
    {
      title: "About Us",
      subtitle: "Our story and mission",
      icon: Users,
      path: "/docs/about",
      color: "bg-gradient-to-br from-blue-500 to-indigo-600",
    },
    {
      title: "Investor Info",
      subtitle: "Investment opportunity",
      icon: TrendingUp,
      path: "/docs/investors",
      color: "bg-gradient-to-br from-purple-500 to-violet-600",
    },
  ];

  const filteredCategories = documentCategories.filter((cat) =>
    cat.documents.some(
      (doc) =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-800">MyMeetingsPal</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate("/pricing")}>
                Pricing
              </Button>
              <Button onClick={() => navigate("/login")}>
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-16 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full text-teal-700 text-sm font-medium mb-6">
            <BookOpen className="w-4 h-4" />
            Documentation Library
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Everything You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-purple-600">Know</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Comprehensive guides, investor information, and resources to help you get the most out of MyMeetingsPal.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
        </div>
      </section>

      {/* Featured Documents */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          {featuredDocs.map((doc) => (
            <button
              key={doc.path}
              onClick={() => navigate(doc.path)}
              className="group relative overflow-hidden rounded-2xl p-8 text-left transition-all hover:scale-[1.02] hover:shadow-xl"
            >
              <div className={`absolute inset-0 ${doc.color}`} />
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <doc.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{doc.title}</h3>
                <p className="text-white/80 text-sm">{doc.subtitle}</p>
                <div className="flex items-center gap-2 mt-4 text-white/90 text-sm font-medium group-hover:gap-3 transition-all">
                  Read Now <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Document Categories */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-slate-800 mb-8">Browse by Category</h2>
        <div className="space-y-8">
          {(searchQuery ? filteredCategories : documentCategories).map((category) => (
            <div key={category.id} className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                    <category.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">{category.title}</h3>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {category.documents
                  .filter(
                    (doc) =>
                      !searchQuery ||
                      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      doc.description.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((doc) => (
                    <button
                      key={doc.path}
                      onClick={() => navigate(doc.path)}
                      className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors text-left group"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">
                            {doc.title}
                          </h4>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {doc.readTime}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">{doc.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {doc.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-center">
          <Sparkles className="w-12 h-12 text-teal-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need More Information?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Our team is here to help. Whether you&apos;re a potential customer, investor, or partner, 
            we&apos;d love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => window.location.href = "mailto:hello@mymeetingspal.com"}
              className="bg-white text-slate-800 hover:bg-slate-100 px-8 h-12"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
            <Button
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700 px-8 h-12"
              onClick={() => navigate("/pricing")}
            >
              View Pricing
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-white">MyMeetingsPal</span>
              </div>
              <p className="text-sm text-slate-400">
                Professional corporate minutes for modern governance teams.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate("/docs/user-guide")} className="hover:text-white transition-colors">User Guide</button></li>
                <li><button onClick={() => navigate("/docs/user-guide#governance")} className="hover:text-white transition-colors">Governance Features</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate("/docs/about")} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => navigate("/docs/investors")} className="hover:text-white transition-colors">Investors</button></li>
                <li><a href="mailto:hello@mymeetingspal.com" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Connect</h4>
              <div className="flex items-center gap-3">
                <a href="#" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-slate-600 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-slate-600 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-slate-600 transition-colors">
                  <Globe className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-8 text-center text-sm">
            <p>© {new Date().getFullYear()} MyMeetingsPal. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
